const DUTCH_ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'ir', 'drs', 'ing', 'bc',
  'dhr', 'mw', 'mevr',
  'st', 'str', 'ave', 'blvd',
  'jan', 'feb', 'mrt', 'apr', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec',
  'nr', 'no', 'vs', 'ca', 'bijv', 'b.v', 'o.a', 'i.v.m', 'i.p.v', 't.o.v',
  'enz', 'etc', 'nl', 'zgn', 'resp', 'max', 'min', 'gem', 'fig', 'blz', 'pag',
  'tel', 'fax', 'ref', 'dept', 'div', 'vol', 'pp', 'ed', 'red',
  'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm',
  'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
]);

function normalizeText(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{2,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

function isDutchAbbreviation(word: string): boolean {
  const lower = word.toLowerCase().replace(/\.$/, '');
  if (DUTCH_ABBREVIATIONS.has(lower)) return true;
  if (/^\d+$/.test(lower)) return true;
  if (/^[a-z]\.$/.test(word.toLowerCase())) return true;
  return false;
}

export function splitIntoSentences(text: string): string[] {
  if (!text || !text.trim()) return [];

  const normalized = normalizeText(text);
  const paragraphs = normalized.split(/\n\n+/);
  const result: string[] = [];

  for (const paragraph of paragraphs) {
    const para = paragraph.replace(/\n/g, ' ').replace(/[ \t]+/g, ' ').trim();
    if (!para) continue;

    const paraResult: string[] = [];
    let current = '';
    let i = 0;
    let quoteDepth = 0;

    const OPEN_QUOTES = new Set(['"', '\u201C', '\u2018', '\u00AB', '(']);
    const CLOSE_QUOTES = new Set(['"', '\u201D', '\u2019', '\u00BB', ')']);

    while (i < para.length) {
      const char = para[i];

      if (OPEN_QUOTES.has(char)) {
        quoteDepth++;
        current += char;
        i++;
        continue;
      }

      if (CLOSE_QUOTES.has(char)) {
        if (quoteDepth > 0) quoteDepth--;
        current += char;
        i++;
        continue;
      }

      if (char === '.' && para[i + 1] === '.' && para[i + 2] === '.') {
        current += '...';
        i += 3;
        const afterEllipsis = i;
        let j = afterEllipsis;
        while (j < para.length && para[j] === ' ') j++;
        const nextChar = para[j] || '';
        if (nextChar && /[A-Z\u00C0-\u00D6\u00D8-\u00DE\u0100-\u017E]/.test(nextChar) && j > afterEllipsis) {
          if (current.trim()) paraResult.push(current.trim());
          current = '';
          i = j;
        }
        continue;
      }

      if (/[!?؟\u061F]/.test(char)) {
        current += char;
        i++;
        while (i < para.length && /[!?؟\u061F]/.test(para[i])) {
          current += para[i];
          i++;
        }
        if (para[i] === '"' || para[i] === '\u201D' || para[i] === '\u2019' || para[i] === '\u00BB') {
          current += para[i];
          i++;
          if (quoteDepth > 0) quoteDepth--;
        }
        while (i < para.length && para[i] === ' ') i++;
        if (quoteDepth === 0 && current.trim()) {
          paraResult.push(current.trim());
          current = '';
        }
        continue;
      }

      if (char === '.') {
        const wordsBefore = current.trimEnd().split(/\s+/);
        const lastWord = wordsBefore[wordsBefore.length - 1] || '';

        if (isDutchAbbreviation(lastWord) || isDutchAbbreviation(lastWord + '.')) {
          current += char;
          i++;
          continue;
        }

        current += char;
        i++;

        if (para[i] === '"' || para[i] === '\u201D' || para[i] === '\u2019' || para[i] === '\u00BB') {
          current += para[i];
          i++;
          if (quoteDepth > 0) quoteDepth--;
        }

        let j = i;
        while (j < para.length && para[j] === ' ') j++;
        const nextNonSpace = para[j] || '';

        if (!nextNonSpace) {
          if (current.trim()) paraResult.push(current.trim());
          current = '';
          i = j;
          continue;
        }

        const isNextUpper = /[A-Z\u00C0-\u00D6\u00D8-\u00DE\u0100-\u017E\u0600-\u06FF]/.test(nextNonSpace);
        const isNextQuote = /["\u201C\u2018\u00AB]/.test(nextNonSpace);

        if (quoteDepth === 0 && (isNextUpper || isNextQuote)) {
          if (current.trim()) paraResult.push(current.trim());
          current = '';
          i = j;
        }
        continue;
      }

      current += char;
      i++;
    }

    if (current.trim()) {
      const trimmed = current.trim();
      if (!/[.!?؟\u061F\u2026]$/.test(trimmed) && !/[.!?؟\u061F\u2026]["'\u201D\u2019\u00BB)]$/.test(trimmed)) {
        paraResult.push(trimmed + '.');
      } else {
        paraResult.push(trimmed);
      }
    }

    result.push(...paraResult);
  }

  return result.length > 0 ? result : [text.trim()];
}

export function extractWords(sentence: string): string[] {
  return sentence
    .toLowerCase()
    .replace(/[.,!?؟;:()'"\-]/g, '')
    .split(/\s+/)
    .filter(Boolean);
}

export function normalizeForSpeechRecognition(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function extractNormalizedWords(text: string): string[] {
  return normalizeForSpeechRecognition(text)
    .split(/\s+/)
    .filter(Boolean);
}

export function ensureConsistentSegmentation(
  originalText: string,
  translationText: string
): { original: string[], translation: string[] } {
  const originalSegments = splitIntoSentences(originalText);
  const translationSegments = splitIntoSentences(translationText);

  if (originalSegments.length !== translationSegments.length) {
    console.warn(
      `Segment count mismatch: ${originalSegments.length} original segments vs ${translationSegments.length} translation segments`
    );

    const maxLength = Math.max(originalSegments.length, translationSegments.length);
    return {
      original: originalSegments.concat(Array(maxLength - originalSegments.length).fill('')),
      translation: translationSegments.concat(Array(maxLength - translationSegments.length).fill(''))
    };
  }

  return {
    original: originalSegments,
    translation: translationSegments
  };
}
